import { Box, Typography } from '@mui/material';
import { type MouseEvent, useState } from 'react';

import Description from '../../components/Description/Description';
import Information from '../../components/Information/Information';
import InformationRow from '../../components/Information/InformationRow';
import RiskColor from '../../components/RiskColor/RiskColor';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Tabs from '../../components/Tabs/Tabs';
import ToggleOfferField from '../../components/ToggleOfferField/ToggleOfferField';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { formatYearsAndMonths } from '../../helpers/formatYearsAndMonths';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { CountryCode } from '../../types/CountryCode';
import { Offer, Offers } from '../../types/OfferTypes';
import ConsolidationsList from './ConsolidationsList';

type Props = {
    selectedOffer: Offer;
    availableOffers: Offers;
    selected: string | undefined;
    onSelectOffer: (event: MouseEvent<HTMLElement>, offerId: string) => void;
};

const SelectOfferPageSidePanel = ({ selectedOffer, availableOffers, selected, onSelectOffer }: Props) => {
    const translate = useTranslation();
    const { countryCode } = useSettings();
    const [activeTab, setActiveTab] = useState<'details' | 'loans'>('details');

    const hasConsolidations = !!selectedOffer.consolidations && selectedOffer.consolidations.length > 0;
    const hasCoApplicantLoan = selectedOffer.consolidations?.some(
        (consolidation) => consolidation.applicantType === 'CO',
    );
    const noApplicantType = selectedOffer.consolidations?.some((consolidation) => !consolidation.applicantType);

    return (
        <Box data-testid="selected-offer-box">
            <ToggleOfferField offer={availableOffers} selected={selected} setSelected={onSelectOffer} />

            {availableOffers.offers.length > 1 ? (
                <Box data-testid="selected-offer-description" mt={2}>
                    {selectedOffer.offerType === 'CASH' && (
                        <Description
                            text={
                                translate('page.select-offer.description.cash') +
                                ` <b>${formatNumberByThousands(
                                    selectedOffer.approved.amount,
                                )} ${translate('field.currency.label')}</b>.`
                            }
                        />
                    )}
                    {selectedOffer.offerType === 'REQUESTED_CONSOLIDATION' && (
                        <Description
                            text={translate('page.select-offer.description.requested-consolidation')}
                        />
                    )}
                    {selectedOffer.offerType === 'MAX_CONSOLIDATION' && (
                        <Description
                            text={translate('page.select-offer.description.max-consolidation')}
                        />
                    )}
                    <ShowDetailsModal
                        label={translate('dialog.which-loan.label')}
                        dialogTitle={translate('dialog.which-loan.label')}
                        dialogContent={translate('dialog.which-loan.content')}
                    />
                </Box>
            ) : null}

            {hasConsolidations && (
                <Tabs
                    tabs={[
                        {
                            label: translate('page.select-offer.tab.details.title'),
                            value: 'details',
                        },
                        { label: translate('page.select-offer.tab.loans.title'), value: 'loans' },
                    ]}
                    value={activeTab}
                    onChange={(val) => setActiveTab(val as 'details' | 'loans')}
                />
            )}

            {hasConsolidations && activeTab === 'loans' && (
                <>
                    {noApplicantType ? (
                        <ConsolidationsList consolidations={selectedOffer.consolidations!} />
                    ) : hasCoApplicantLoan ? (
                        <>
                            <Typography variant="subtitle1" fontSize={'14px'}>
                                {translate('field.consolidations.applicant.subtitle')}
                            </Typography>
                            <ConsolidationsList
                                consolidations={selectedOffer.consolidations!.filter(
                                    (o) => o.applicantType === 'MAIN',
                                )}
                            />
                            <Typography variant="subtitle1" fontSize={'14px'} pt={'12px'}>
                                {translate('field.consolidations.co-applicant.subtitle')}
                            </Typography>
                            <ConsolidationsList
                                consolidations={selectedOffer.consolidations!.filter(
                                    (o) => o.applicantType === 'CO',
                                )}
                            />
                        </>
                    ) : (
                        <ConsolidationsList consolidations={selectedOffer.consolidations!} />
                    )}
                </>
            )}

            {(!hasConsolidations || activeTab === 'details') && (
                <>
                    <Information>
                        <InformationRow
                            name={translate('field.amount.label')}
                            value={`${formatNumberByThousands(selectedOffer.approved.amount)} ${translate(
                                'field.currency.label',
                            )}`}
                        />
                        {selectedOffer.preliminaryDisbursement &&
                            selectedOffer.preliminaryDisbursement.amount > 0 && (
                                <InformationRow
                                    name={translate('field.preliminary-disbursement.label')}
                                    value={`${formatNumberByThousands(
                                        selectedOffer.preliminaryDisbursement.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                            )}
                        <InformationRow
                            name={translate('field.required-consolidation.label')}
                            value={`${formatNumberByThousands(
                                selectedOffer.requiredConsolidation.amount,
                            )} ${translate('field.currency.label')}`}
                        />
                        <InformationRow
                            name={translate('field.monthly-installment.label')}
                            value={`${formatNumberByThousands(
                                selectedOffer.monthlyInstallment.amount,
                            )} ${translate('field.currency.label')}`}
                        />
                        <InformationRow
                            name={translate('field.tenor.label')}
                            value={`${formatYearsAndMonths(Number(selectedOffer.tenor.months), translate)}`}
                        />
                        <InformationRow
                            name={translate('field.nominal-interest.label')}
                            value={`${selectedOffer.nominalInterest?.percent} %`}
                        />
                        <InformationRow
                            name={translate('field.effective-interest.label')}
                            value={`${selectedOffer.effectiveInterest.percent} %`}
                        />
                    </Information>
                    {countryCode === CountryCode.NO && <RiskColor riskColor={selectedOffer.riskColor} />}
                </>
            )}
        </Box>
    );
};

export default SelectOfferPageSidePanel;
